import React, { useEffect, useMemo, useState } from 'react';
import { Info, ScrollText, Search, Unlock, X } from 'lucide-react';
import { Audit } from '../interfaces/AuditInterface';

/**
 * Audit log side panel for the v2 Timesheet Review console
 * (design_handoff_timesheet_audit_approvals, View 3).
 *
 * Chrome deliberately mirrors the payroll-edit Change history panel
 * (aside.pe-panel) — WCDP users see both, so title + sub-note are the only
 * differences. Rows are uniform (time · description): the description is one
 * free-text pipe-delimited string with no structured actor/entity/field, so it
 * is styled (pipe separators, dimmed `Key:` prefixes) but never parsed
 * positionally. The one safe piece of prefix matching is the fixed
 * "Timesheet Unlocked" writer string, which drives the round grouping — the
 * whole point of the panel, since unlocking deletes the approval rows and this
 * trail is the only surviving record of a prior round.
 *
 * Audit rows carry no per-row offset, but they are true instants: the server
 * stores UTC and the driver tags the wire value with `Z`. They are therefore
 * rendered in the VIEWER'S OWN zone — the clock the reader is checking the log
 * against — with the zone named once in the panel note rather than per row.
 */

interface AuditLogPanelProps {
  open: boolean;
  onClose: () => void;
  employeeName: string;
  periodLabel: string;
  rows: Audit[] | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON_ORDERED = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/**
 * The wire value as an instant, then read through the browser's own zone.
 * A stamp that arrives WITHOUT a zone marker is UTC by contract (the server
 * writes `new Date()` into a UTC-mode driver), so tag it before parsing —
 * otherwise Date.parse would read the digits as the viewer's local wall clock
 * and the log would be off by the viewer's offset.
 */
function local(iso: string): Date {
  const text = (iso ?? '').trim();
  return new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(text) ? text : `${text}Z`);
}

function clock(d: Date): string {
  let h = d.getHours();
  const ap = h < 12 ? 'AM' : 'PM';
  h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${ap}`;
}

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const dayHead = (d: Date) =>
  `${DOW[d.getDay()]}, ${MON_ORDERED[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
const shortStamp = (d: Date) => {
  const dow = DOW[d.getDay()];
  const mon = MON_ORDERED[d.getMonth()];
  return `${dow.charAt(0)}${dow.slice(1).toLowerCase()} ${mon.charAt(0)}${mon.slice(1).toLowerCase()} ${d.getDate()}`;
};

/** "PDT" — the viewer's own zone, named once in the note so the times below
 *  need no per-row tag. Falls back to empty on a runtime that won't name it. */
function localZoneLabel(): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
}

const isUnlockRow = (r: Audit) => /^\s*Timesheet Unlocked/i.test(r.description ?? '');

/** Style — never parse — the free-text description: pipes become separators and
 *  `Key:` prefixes are dimmed; anything unmatched falls through as plain text. */
function DescText({ text }: { text: string }) {
  const parts = (text ?? '').split('|');
  return (
    <>
      {parts.map((part, i) => {
        const t = part.trim();
        const kv = /^([A-Za-z][\w /-]{0,28}):\s([\s\S]+)$/.exec(t);
        return (
          <React.Fragment key={i}>
            {i > 0 && <span className="sep">|</span>}{' '}
            {kv ? (<><span className="k">{kv[1]}:</span> {kv[2]}</>) : t}{' '}
          </React.Fragment>
        );
      })}
    </>
  );
}

interface RoundGroup {
  rows: Audit[];
  /** The unlock event that ended this round (renders as the divider band below the group). */
  unlockAfter: Audit | null;
}

export default function AuditLogPanel({
  open, onClose, employeeName, periodLabel, rows, loading, error, onRetry,
}: AuditLogPanelProps) {
  const [filter, setFilter] = useState('');
  const zoneLabel = useMemo(localZoneLabel, []);

  useEffect(() => {
    if (!open) setFilter('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Endpoint has no ORDER BY — sort client-side, newest first (v1 parity).
  const sorted = useMemo(
    () => (rows ? [...rows].sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()) : null),
    [rows]
  );

  const groups: RoundGroup[] = useMemo(() => {
    if (!sorted) return [];
    const out: RoundGroup[] = [{ rows: [], unlockAfter: null }];
    sorted.forEach((r) => {
      if (isUnlockRow(r)) {
        out[out.length - 1].unlockAfter = r;
        out.push({ rows: [], unlockAfter: null });
      } else {
        out[out.length - 1].rows.push(r);
      }
    });
    return out;
  }, [sorted]);

  const q = filter.trim().toLowerCase();
  const filtered = useMemo(
    () => (q && sorted ? sorted.filter((r) => (r.description ?? '').toLowerCase().includes(q)) : null),
    [sorted, q]
  );

  if (!open) return null;

  const renderRows = (list: Audit[]) => {
    let lastDay: string | null = null;
    return list.map((r) => {
      const when = local(r.dateTime);
      const k = dayKey(when);
      const head = k !== lastDay ? <div className="etrc-ap-dayhead">{dayHead(when)}</div> : null;
      lastDay = k;
      return (
        <React.Fragment key={r.auditID}>
          {head}
          <div className="etrc-ap-row">
            <div className="etrc-ap-time">{clock(when)}</div>
            <div className="etrc-ap-desc"><DescText text={r.description} /></div>
          </div>
        </React.Fragment>
      );
    });
  };

  const renderBody = () => {
    if (loading) return <div className="etrc-ap-loading">Loading audit log…</div>;
    if (error) {
      return (
        <div className="etrc-ap-empty">
          <ScrollText size={26} />
          <div className="t">Couldn’t load the audit log.</div>
          <button type="button" className="etrc-btn small" onClick={onRetry}>Retry</button>
        </div>
      );
    }
    if (!sorted || sorted.length === 0) {
      return (
        <div className="etrc-ap-empty">
          <ScrollText size={26} />
          <div className="t">No audit data available.</div>
          <div className="s">Nothing has been recorded for this employee in this pay period yet.</div>
        </div>
      );
    }
    // Filtering suspends the round structure: a flat, day-headed list of matches.
    if (filtered) {
      if (filtered.length === 0) {
        return (
          <div className="etrc-ap-empty">
            <ScrollText size={26} />
            <div className="t">No events match your filter.</div>
          </div>
        );
      }
      return renderRows(filtered);
    }
    const total = groups.length;
    return groups.map((g, i) => {
      const roundNo = total - i;
      const current = i === 0;
      const unlockStamp = g.unlockAfter ? local(g.unlockAfter.dateTime) : null;
      return (
        <React.Fragment key={`round-${roundNo}`}>
          <div className="etrc-ap-round">
            {total > 1 && (
              <div className={`etrc-ap-roundhead${current ? ' cur' : ''}`}>
                <span className="pip">Round {roundNo}</span>
                {current ? 'Current — approvals on the card above' : 'Superseded — approvals deleted at unlock'}
              </div>
            )}
            {renderRows(g.rows)}
          </div>
          {g.unlockAfter && unlockStamp && (
            <div className="etrc-ap-divider">
              <Unlock size={16} />
              <div>
                <div className="t">Timesheet unlocked · {shortStamp(unlockStamp)} · {clock(unlockStamp)}</div>
                <div className="s"><DescText text={g.unlockAfter.description} /></div>
              </div>
            </div>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <>
      <div className="etrc-ap-scrim" onClick={onClose} />
      <aside className="etrc-ap-panel" role="dialog" aria-label="Audit log">
        <div className="etrc-ap-head">
          <ScrollText size={18} color="#5a6573" />
          <div className="etrc-ap-head-main">
            <h3>Audit log</h3>
            <div className="sub">{employeeName} · {periodLabel}</div>
          </div>
          <button type="button" className="etrc-ap-close" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>
        <div className="etrc-ap-note">
          <Info size={14} />
          Every recorded event for this pay period, newest first — including rounds cleared by an unlock.
          Times are shown in your local time{zoneLabel ? ` (${zoneLabel})` : ''}.
        </div>
        <div className="etrc-ap-filter">
          <Search size={14} />
          <input
            type="text"
            value={filter}
            placeholder="Filter audit events…"
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="etrc-ap-body">{renderBody()}</div>
      </aside>
    </>
  );
}
